/**
 * Inline formatting for status-report text: bold, italic, underline, and the
 * Danone highlighter.
 *
 * Text is STORED as plain text carrying lightweight markers (`**bold**`,
 * `*italic*`, `__underline__`, `==highlight==`) even though the editor is
 * WYSIWYG. The markers are an internal wire format, never shown to the user:
 *
 *  - No injection surface. The slide renders through React elements, and the
 *    editor's HTML is generated here from escaped text, so nothing untrusted
 *    is ever handed to innerHTML and no sanitiser dependency is needed.
 *  - Downstream stays simple. The same string goes to Cosmos, the CSV
 *    exports, and (phase 2) the AI that compresses these narratives into the
 *    DDI portfolio slide. Stored HTML would need stripping at every one of
 *    those boundaries; `stripMarkup` handles it in one call.
 *  - No new dependency. TipTap or Lexical is a large amount of machinery for
 *    four marks.
 *
 * Three representations, one source of truth:
 *   markers  -> React nodes   `renderRich`  (the slide)
 *   markers  -> HTML string   `toHtml`      (loading the editor)
 *   DOM      -> markers       `fromDom`     (reading the editor back)
 */

import type { ReactNode } from 'react'

/**
 * The Danone highlighter (brand book p.60): a solid block of Danone Blue with
 * white text. The brand book is explicit that highlighted text is always
 * white, never blue on a tint. `boxDecorationBreak` keeps the block intact
 * when a highlighted phrase wraps across two lines.
 */
export const HIGHLIGHT_CSS = {
  background: '#005EB8',
  color: '#ffffff',
  padding: '1px 6px 3px',
  borderRadius: '3px',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
} as const

const HIGHLIGHT_INLINE =
  'background:#005EB8;color:#ffffff;padding:1px 6px 3px;border-radius:3px;' +
  '-webkit-box-decoration-break:clone;box-decoration-break:clone'

// Markers must not sit against whitespace, so arithmetic and prose like
// "5 * 3" or "a * b * c" never turn into accidental italics. Bold is matched
// before italic because `**` also satisfies the italic pattern.
// Groups: 1 = bold, 2 = underline, 3 = highlight, 4 = italic.
const TOKEN =
  /\*\*(?!\s)([\s\S]+?)(?<!\s)\*\*|__(?!\s)([\s\S]+?)(?<!\s)__|==(?!\s)([\s\S]+?)(?<!\s)==|\*(?!\s)([\s\S]+?)(?<!\s)\*/g

/**
 * A line beginning with "- " is a bullet. Bullets are a block construct, not an
 * inline mark, so they live in the line structure rather than in TOKEN - which
 * also keeps the stored text readable as plain text and lets the AI work
 * (phase 2) treat a list as a list.
 */
export const BULLET = '- '

export type Block =
  | { type: 'ul'; items: string[] }
  | { type: 'lines'; lines: string[] }

/**
 * Split marker text into bullet lists and runs of plain lines.
 *
 * Consecutive bullet lines group into one list; everything else accumulates
 * into a run of lines the caller renders as paragraphs. Blank lines are
 * dropped, so a stray newline never produces an empty paragraph on the slide.
 */
export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = []
  for (const raw of (text ?? '').split('\n')) {
    const line = raw.trim()
    if (!line) continue
    const isBullet = line.startsWith(BULLET.trim() + ' ') || line === BULLET.trim()
    const body = isBullet ? line.slice(1).trim() : line
    const last = blocks[blocks.length - 1]
    if (isBullet) {
      if (!body) continue
      if (last?.type === 'ul') last.items.push(body)
      else blocks.push({ type: 'ul', items: [body] })
    } else {
      if (last?.type === 'lines') last.lines.push(body)
      else blocks.push({ type: 'lines', lines: [body] })
    }
  }
  return blocks
}

export type MarkKind = 'bold' | 'italic' | 'underline' | 'highlight'

export const MARKERS: Record<MarkKind, string> = {
  bold: '**',
  italic: '*',
  underline: '__',
  highlight: '==',
}

/**
 * Parse marked-up text into React nodes, for the slide. Unmatched or
 * half-typed markers are left as literal characters so an in-progress `**`
 * never blanks the slide.
 */
export function renderRich(text: string, keyPrefix = 'r'): ReactNode[] {
  if (!text) return []
  const nodes: ReactNode[] = []
  const re = new RegExp(TOKEN)
  let last = 0
  let m: RegExpExecArray | null
  let i = 0

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    const key = `${keyPrefix}${i++}`
    if (m[1] !== undefined) {
      nodes.push(<strong key={key}>{renderRich(m[1], `${key}b`)}</strong>)
    } else if (m[2] !== undefined) {
      nodes.push(<u key={key}>{renderRich(m[2], `${key}u`)}</u>)
    } else if (m[3] !== undefined) {
      nodes.push(<mark key={key} style={HIGHLIGHT_CSS}>{renderRich(m[3], `${key}h`)}</mark>)
    } else {
      nodes.push(<em key={key}>{renderRich(m[4] as string, `${key}i`)}</em>)
    }
    last = re.lastIndex
  }

  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')

/** Inline marks within a single line, escaped. */
function inlineHtml(text: string): string {
  if (!text) return ''
  const re = new RegExp(TOKEN)
  let out = ''
  let last = 0
  let m: RegExpExecArray | null

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out += esc(text.slice(last, m.index))
    if (m[1] !== undefined) out += `<strong>${inlineHtml(m[1])}</strong>`
    else if (m[2] !== undefined) out += `<u>${inlineHtml(m[2])}</u>`
    else if (m[3] !== undefined) out += `<mark style="${HIGHLIGHT_INLINE}">${inlineHtml(m[3])}</mark>`
    else out += `<em>${inlineHtml(m[4] as string)}</em>`
    last = re.lastIndex
  }

  if (last < text.length) out += esc(text.slice(last))
  return out
}

/**
 * Marked-up text to an HTML string for the editor's contentEditable. Every
 * text run is escaped, so the only tags in the result are the ones emitted
 * here.
 */
export function toHtml(text: string): string {
  if (!text) return ''
  // Blocks rather than <br>-joined text: a <br> cannot carry a margin, so
  // paragraphs sat flush in the editor while the slide spaced them.
  //
  // The very first line is left bare, because that is exactly what the browser
  // does when you type - line one is a text node, every Enter after it makes a
  // div. Matching that means a loaded narrative and a freshly typed one have
  // the same shape, so one CSS rule spaces both.
  let first = true
  const line = (html: string) => {
    if (first) { first = false; return html }
    return `<div>${html}</div>`
  }
  return parseBlocks(text)
    .map(b => {
      if (b.type === 'ul') {
        first = false
        return `<ul>${b.items.map(i => `<li>${inlineHtml(i)}</li>`).join('')}</ul>`
      }
      return b.lines.map(l => line(inlineHtml(l))).join('')
    })
    .join('')
}

/** Which marker an element represents, or null if it carries no formatting. */
function markerFor(el: HTMLElement): string | null {
  switch (el.tagName.toLowerCase()) {
    case 'strong': case 'b': return MARKERS.bold
    case 'em': case 'i': return MARKERS.italic
    case 'u': return MARKERS.underline
    case 'mark': return MARKERS.highlight
  }
  // execCommand can emit styled spans instead of tags depending on browser
  // and on whether styleWithCSS was honoured, so read the style too.
  const s = el.style
  if (s.fontWeight === 'bold' || s.fontWeight === '700') return MARKERS.bold
  if (s.fontStyle === 'italic') return MARKERS.italic
  if (s.textDecoration.includes('underline')) return MARKERS.underline
  if (s.backgroundColor) return MARKERS.highlight
  return null
}

/**
 * Read a contentEditable's DOM back into marker text.
 *
 * Browsers represent line breaks inconsistently (`<br>` in some cases, a
 * wrapping `<div>` per line in others), so both are normalised to `\n`.
 * Markers around an empty element are dropped rather than emitted as a stray
 * `****`, which would otherwise accumulate as the user edits.
 */
export function fromDom(root: HTMLElement): string {
  let out = ''

  const walk = (node: Node) => {
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent ?? ''
        return
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return

      const el = child as HTMLElement
      if (el.tagName.toLowerCase() === 'br') {
        out += '\n'
        return
      }

      const mark = markerFor(el)
      if (mark) {
        const at = out.length
        walk(el)
        const inner = out.slice(at)
        // Markers can never sit against whitespace - that guard is what stops
        // "5 * 3" becoming italics - so a marker pair wrapping whitespace would
        // fail to re-parse and show up literally on the slide. Two cases:
        //
        //  - Nothing but whitespace inside. Happens constantly: pressing Enter
        //    inside bold text makes the browser carry the <b> onto the new
        //    empty line as <b><br></b>. Keep the break, drop the markers.
        //  - Padding around real content, e.g. <b>Hello </b>. Move the
        //    whitespace outside the markers so they still hug the text.
        const [, lead, core, trail] = inner.match(/^(\s*)([\s\S]*?)(\s*)$/) as RegExpMatchArray
        out = core
          ? `${out.slice(0, at)}${lead}${mark}${core}${mark}${trail}`
          : out.slice(0, at) + inner
        return
      }

      // Block-level wrappers start a new line, but only if we are not already
      // at the start of one. List items additionally carry the bullet marker.
      const tag = el.tagName.toLowerCase()
      const isBlock = tag === 'div' || tag === 'p' || tag === 'ul' || tag === 'ol' || tag === 'li'
      // A break both before AND after: without the trailing one, text following
      // a list ran straight onto the last bullet ("- bOutro"). The guard keeps
      // it from doubling up.
      if (isBlock && out && !out.endsWith('\n')) out += '\n'
      if (tag === 'li') out += BULLET
      walk(el)
      if (isBlock && out && !out.endsWith('\n')) out += '\n'
    })
  }

  walk(root)
  // Browsers park a trailing <br> or empty <div> at the end of an editable to
  // keep the caret placeable; that is chrome, not content.
  return out.replace(/\n+$/, '')
}

/** The same text with all markers removed. For exports, character counts, and
 *  the phase-2 AI input. */
export function stripMarkup(text: string): string {
  if (!text) return ''
  return text.replace(TOKEN, (_full, b, u, h, i) => stripMarkup(b ?? u ?? h ?? i ?? ''))
}
